/**
 * Shared recursive leaf-walk for the Human Call tab models.
 *
 * {@link walkTemplateToLeaves} descends a schema {@link FormField} template
 * (scalar / object / array) against a live value and emits a FLAT list of
 * {@link TreeLeaf}s — one per leaf scalar — keyed with the concrete output path
 * (`drug[0].coverage.copay`). Each leaf carries the two grouping keys the panes
 * need to render nesting WITHOUT a heavy tree structure:
 *   - `instance` — the entity-array element it lives under (`drug[0]` + a code/name
 *      label), or undefined for a top-level field;
 *   - `section`  — the nearest enclosing nested-object label ("Coverage"), or
 *      undefined for a direct entity/top-level leaf.
 *
 * The board model ({@link ./fieldBoardModel}) and the script model
 * ({@link ./scriptModel}) both reuse this, so the flatten/keying rules live in
 * exactly one place. PURE (no React/IO) — unit-tested in isolation.
 */
import type { FormField } from '@/types/formSchema';

import type { FieldStatus } from './fieldStatus';
import { computeStatus, isEmptyValue } from './fieldStatus';

/**
 * Maps an entity output key to the case INPUT array of billable codes that drives its
 * fan-out. The output entity (`drug`/`procedure`/`sites_of_care`) is populated from a
 * differently-named input array, so both the guided script and the "All fields" board
 * seed one instance per input code even before the output slot is filled.
 */
export const ENTITY_INPUT_KEY: Record<string, string> = {
  drug: 'j_code',
  procedure: 'cpt_code',
  sites_of_care: 'sites_of_care',
  // MEDBHOBV (BV-2328) carries a medbv_ and a bho_ copy of each entity array; both
  // fan out over the same input codes.
  medbv_drug: 'j_code',
  bho_drug: 'j_code',
  medbv_procedure: 'cpt_code',
  bho_procedure: 'cpt_code',
  medbv_sites_of_care: 'sites_of_care',
  bho_sites_of_care: 'sites_of_care',
};

/** Status/required lookup sets, keyed by full output path. */
export interface StatusCtx {
  missing: Set<string>;
  required: Set<string>;
  heard: Set<string>;
  edited: Set<string>;
}

/** The entity-array element a leaf belongs to (e.g. `drug[0]`). */
export interface LeafInstance {
  /** Indexed entity key, e.g. `drug[0]`. */
  key: string;
  /** Human header: the element's `item_identifier_field`/`code`/`name`, else 1-based index. */
  label: string;
}

/** One resolved leaf field with its live status + grouping keys. */
export interface TreeLeaf {
  /** Full output path: `plan_name` | `drug[0].coverage.copay`. */
  key: string;
  label: string;
  field: FormField;
  type: FormField['type'];
  value: unknown;
  status: FieldStatus;
  required: boolean;
  /** Entity instance this leaf lives under; undefined for top-level leaves. */
  instance?: LeafInstance;
  /** Nearest enclosing nested-object label; undefined for a direct leaf. */
  section?: string;
}

/** A usable scalar label, or undefined for empty / non-scalar (e.g. a misconfigured object). */
function scalarLabel(value: unknown): string | undefined {
  if (isEmptyValue(value) || typeof value === 'object') return undefined;
  return String(value);
}

/** The element's display label: item_identifier_field / code / name, else 1-based index. */
export function instanceLabel(
  field: FormField,
  element: Record<string, unknown>,
  index: number,
): string {
  const idField = field.item_identifier_field;
  return (
    (idField ? scalarLabel(element[idField]) : undefined) ??
    scalarLabel(element.code) ??
    scalarLabel(element.name) ??
    String(index + 1)
  );
}

/**
 * Flatten a scalar/object/array field template into leaves under `keyBase`.
 *
 * - scalar (incl. a type-`array`/`multi_select` field with NO nested `fields` — a repeated
 *   scalar/enum whose value is a list) → one leaf (carrying the current `instance` + `section`);
 * - object → recurse children with `section = field.label` (instance unchanged);
 * - array WITH nested `fields` (a repeatable sub-record) → recurse each element's children
 *   with a fresh `instance` and section reset.
 *
 * `section` (optional) seeds the enclosing-object label for a leaf ref reached via a
 * dotted path (the script model resolves `coverage.copay` to the copay def but wants
 * it grouped under "Coverage").
 */
export function walkTemplateToLeaves(
  field: FormField,
  keyBase: string,
  valueAt: unknown,
  ctx: StatusCtx,
  section?: string,
  instance?: LeafInstance,
): TreeLeaf[] {
  if (field.type === 'object') {
    const childVal = (valueAt ?? {}) as Record<string, unknown>;
    return (field.fields ?? []).flatMap(child =>
      walkTemplateToLeaves(
        child,
        `${keyBase}.${child.id}`,
        childVal[child.id],
        ctx,
        field.label,
        instance,
      ),
    );
  }

  // A `fields`-less array is a repeated scalar/enum (a multi-value LEAF), not a sub-record —
  // treat it as a leaf (below). Only arrays WITH a nested template fan out into instances.
  if (field.type === 'array' && (field.fields?.length ?? 0) > 0) {
    const arr = Array.isArray(valueAt) ? valueAt : [];
    return arr.flatMap((raw, i) => {
      const element = (raw ?? {}) as Record<string, unknown>;
      const elemInstance: LeafInstance = {
        key: `${keyBase}[${i}]`,
        label: instanceLabel(field, element, i),
      };
      return (field.fields ?? []).flatMap(child =>
        // Entering an element establishes a fresh instance and resets the section.
        walkTemplateToLeaves(
          child,
          `${elemInstance.key}.${child.id}`,
          element[child.id],
          ctx,
          undefined,
          elemInstance,
        ),
      );
    });
  }

  return [
    {
      key: keyBase,
      label: field.label,
      field,
      type: field.type,
      value: valueAt,
      status: computeStatus(keyBase, valueAt, ctx.missing, ctx.heard, ctx.edited),
      required: ctx.required.has(keyBase),
      instance,
      section,
    },
  ];
}
